import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, } from "@mui/material";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { AppFwError, handleError } from "../../api-client/AppFwApiClient.ts";

interface TextInputDialogProps {
    open: boolean;
    dialogTitle?: string;
    dialogContent?: string;
    inputLabel?: string;
    cancelText?: string;
    confirmText?: string;
    initialValue?: string;
    validate?: (value: string) => boolean;
    onClose: (value: null | string) => Promise<void>;
}

export function TextInputDialog(props: TextInputDialogProps) {

    const [value, setValue] = useState(props.initialValue || "")
    const [isValid, setIsValid] = useState(false)
    const [error, setError] = useState<null | AppFwError>()

    const id = useId();
    const inputRef = useRef<HTMLInputElement>(null)

    const handleFinish = useCallback(async () => {
        try {
            await props.onClose(value.trim())
        } catch (e) {
            setError(await handleError(e))
        }
    }, [props.onClose, value])

    const handleClose = useCallback(async () => {
        setValue("")
        setError(null)
        await props.onClose(null)
    }, [props.onClose])

    const handleValueChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setError(null)
        let newValue = e.target.value
        if (props.validate) {
            setIsValid(props.validate(newValue.trim()))
        }
        setValue(newValue)
    }, [props.validate])

    useEffect(() => {
        if (props.validate) {
            setIsValid(props.validate(value.trim()))
        } else {
            setIsValid(true)
        }
    }, [value, props.validate])

    useEffect(() => {
        if (props.open) {
            setValue(props.initialValue || "")
            setTimeout(() => {
                inputRef.current?.focus()
            }, 100)
        }
    }, [props.open, props.initialValue])

    return (
        <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth="xs"
            sx={{
                "& .MuiDialog-container": {
                    alignItems: "flex-start"
                },
                "& .MuiPaper-root": {
                    mt: "25vh"
                }
            }}
        >
            <DialogTitle>{props.dialogTitle}</DialogTitle>
            <DialogContent>
                <DialogContentText margin="normal">
                    {props.dialogContent}
                </DialogContentText>
                <TextField
                    autoFocus
                    inputRef={inputRef}
                    id={id}
                    label={props.inputLabel}
                    type="text"
                    fullWidth
                    variant="standard"
                    value={value}
                    onChange={handleValueChange}
                    sx={{
                        marginTop: 2,
                    }}
                    error={!!error}
                    helperText={error?.message}
                />
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>{props.cancelText || "Abbrechen"}</Button>
                <Button onClick={handleFinish}
                    disabled={!isValid}>{props.confirmText || "Bestätigen"}</Button>
            </DialogActions>
        </Dialog>
    )
}